export const exams = (exerciseGroups, examExerciseGroups, distributeAutomatically) => ({
  exerciseGroups: exerciseGroups,
  examExerciseGroups: examExerciseGroups,
  distributeAutomatically: distributeAutomatically,

  add(object) {
    const isGroup = object === this.exerciseGroup
    const collection = isGroup ? this.examExerciseGroups : this.examTopics
    const index = isGroup ? this.activeGroups.length + 1 : this.activeTopics.length + 1
    const existingObject = isGroup
      ? collection.find((group) => group.exercise_group_id === object.id)
      : collection.find((topic) => topic.topic_id === object.id)

    // Reopen an existing object with an updated order if it has been marked for deletion
    if (existingObject) {
      existingObject.order = index
      existingObject.destroy = false
      return
    }

    // Add a new empty object if an existing one wasn't found
    if (isGroup) {
      collection.push({
        exercise_group_id: object.id,
        order: index,
        exercise_group: { name: object.name },
        exam_topics: [],
      })
    } else {
      collection.push({
        topic_id: object.id,
        order: index,
        topic: { name: object.name },
      })
    }

    this.distributeQuestions()
  },
  destroy(object) {
    const collection = object === this.examExerciseGroup ? this.examExerciseGroups : this.examTopics
    collection.filter((item) => item.order > object.order).forEach((item) => item.order--)

    // Update the order to 0 for existing records and completely remove the object if it doesn't exist in the database
    object?.id ? (object.order = 0) : collection.splice(collection.indexOf(object), 1)

    this.distributeQuestions()
  },
  // Distribute each exercise group's questions evenly across its child topics if distributeAutomatically is selected
  distributeQuestions() {
    if (this.distributeAutomatically) {
      for (const group of this.activeGroups) {
        const topics = group.exam_topics.filter((topic) => !topic.destroy).sort((a, b) => a.order - b.order)
        const leftoverQuestions = group.questions % topics.length
        const questionsPerTopic = Math.floor(group.questions / topics.length)
        topics.forEach((topic, index) => {
          const addition = index < leftoverQuestions ? 1 : 0
          topic.questions = questionsPerTopic + addition
        })
      }
    }
  },
  formGroupInput(attribute) {
    return `exam[exam_exercise_groups_attributes][${this.examExerciseGroup.exercise_group_id}][${attribute}]`
  },
  // Change the order of exercise groups and topics
  moveUp(object) {
    if (object.order === 1) return
    const collection = object === this.examExerciseGroup ? this.examExerciseGroups : this.examTopics
    collection.find((item) => item.order === object.order - 1).order++
    object.order--

    this.distributeQuestions()
  },
  moveDown(object) {
    const collection = object === this.examExerciseGroup ? this.examExerciseGroups : this.examTopics
    if (object.order === collection.length) return
    collection.find((item) => item.order > object.order).order--
    object.order++

    this.distributeQuestions()
  },
  get activeGroups() {
    return this.examExerciseGroups.filter((group) => !group.destroy)
  },
  get availableExerciseGroups() {
    const selectedGroupIds = this.examExerciseGroups.filter((eeg) => !eeg.destroy).map((eeg) => eeg.exercise_group_id)
    return this.exerciseGroups.filter((eg) => !selectedGroupIds.includes(eg.id))
  },
})

// Functions for the exerciseGroup scope
export const exerciseGroup = (exerciseGroup, licenseClasses) => ({
  exerciseGroup: exerciseGroup,
  licenseClasses: licenseClasses,

  formTopicInput(attribute) {
    return `exam[exam_exercise_groups_attributes][${this.examExerciseGroup.exercise_group_id}][exam_topics_attributes][${this.examTopic.topic_id}][${attribute}]`
  },
  get activeTopics() {
    return this.examTopics.filter((topic) => !topic.destroy)
  },
  get availableTopics() {
    const selectedTopicIds = this.examExerciseGroups.flatMap((eeg) =>
      eeg.exam_topics.filter((topic) => !topic.destroy).map((examTopic) => examTopic.topic_id)
    )
    const exerciseGroup = this.exerciseGroups.find((eg) => eg.id === this.examExerciseGroup.exercise_group_id)
    return exerciseGroup.topics.filter(
      (topic) => !selectedTopicIds.includes(topic.id) && this.licenseClassTopicIds.includes(topic.id)
    )
  },
  // Get the exam topics for the current exercise group
  get examTopics() {
    return this.examExerciseGroup.exam_topics.sort((a, b) => a.order - b.order)
  },
  // Get the IDs of topics that are available for ALL the selected license classes
  get licenseClassTopicIds() {
    const topicIdsByLicenseClass = this.selectedLicenseClasses.map((lc) => lc.topics.map((topic) => topic.id))
    return topicIdsByLicenseClass.flat().filter((id) => topicIdsByLicenseClass.every((idArray) => idArray.includes(id)))
  },
  get topicQuestionsTotal() {
    return this.activeTopics.length
      ? this.activeTopics.map((topic) => topic.questions).reduce((prev, current) => (prev || 0) + (current || 0))
      : 0
  },
  get selectedLicenseClasses() {
    return this.licenseClasses.filter((lc) => this.selected.includes(lc.id))
  },
})
